/**
 * Builds the `PrintContext` snapshot `@rista/printing` runs on: the redux half here, the half only
 * an app can read (device storage, LAN role, permitted payment modes) from the host.
 */
import { catalogHelpers } from "@dotpe/rista-pos-common";
import type { PaymentMethod, PrintContext, PrinterRecord } from "@rista/printing";

import store from "../redux-store";
import { branchTimeOf } from "../time/branchClock";
import {
  selectBusinessData,
  selectChannelDefs,
  selectDefaultPrinter,
  selectLocalPrinters,
  selectPosSettings,
  selectSavedPrinters,
  selectSelectedChannelDefId,
} from "./selectors";

/** What only the app can read. Each field is one existing app helper — see apps/{web,mobile}/src/printing. */
export interface HostSnapshot {
  deviceId: string;
  /** `helper/localServer.isSlave()` */
  isSlave: boolean;
  /** `getLocalStorageWithParse(LOCALSTORAGE_KEYS.printBillFromMaster)` */
  printBillFromMaster: boolean;
  /** `utils/localStorage.getAddedBlePrintersList()` */
  ble: PrinterRecord[];
  /** `utils/localStorage.USBPrinter()` — `{}` when none */
  usb: PrinterRecord;
  /** `helper/paymenthelper.getPaymentMethods()` — permission-filtered, as v1's drawer rule reads them */
  paymentMethods: PaymentMethod[];
  /** `helper/commonHelpers.isPineLabDevice()` — the device prints without a configured printer */
  builtInPrinter?: boolean;
  /** false inside the Android wrapper, where v1 blanks every image before printing */
  canPrintImages?: boolean;
}

/** Channel definitions by mid, with this branch's variant applied — v1 `getChannelDefData`. */
function channelsFor(defs: any[], branchId: string | undefined): Record<string, any> {
  const byMid: Record<string, any> = {};
  for (const def of defs) {
    byMid[def?.mid] = def?.variants?.length ? (catalogHelpers as any).handleChannelVariant(def, branchId) : def;
  }
  return byMid;
}

export function buildPrintContext(host: HostSnapshot, state: any = store.getState()): PrintContext {
  const business = selectBusinessData(state) || {};
  const zone = business.timeZone?.zoneId;
  return {
    now: branchTimeOf(zone),
    business: { branchId: business.branchId, businessType: business.businessType, billTemplate: business.billTemplate },
    posSettings: selectPosSettings(state),
    channels: channelsFor(selectChannelDefs(state), business.branchId),
    selectedChannelDefId: selectSelectedChannelDefId(state),
    paymentMethods: host.paymentMethods || [],
    printers: {
      saved: selectSavedPrinters(state),
      local: selectLocalPrinters(state),
      ble: host.ble || [],
      usb: host.usb || {},
      defaultPrinter: selectDefaultPrinter(state),
    },
    device: {
      id: host.deviceId,
      isSlave: !!host.isSlave,
      printBillFromMaster: !!host.printBillFromMaster,
      builtInPrinter: !!host.builtInPrinter,
      canPrintImages: host.canPrintImages !== false,
    },
  };
}
